import { Server, Settings, Code, CheckCircle, Activity, Globe, RefreshCw } from 'lucide-react';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';

export function MCPServerPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-8 animate-fadeIn">
      <ComingSoonWatermark
        pageKey="mcp-server"
        icon={Server}
        title="MCP Server"
        description="Model Context Protocol server enabling AI assistants to interact with Line4 risk data"
      />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/30">
            <Server className="w-8 h-8 text-purple-500" />
          </div>
          <div>
            <h1 className={cn("text-3xl font-bold", sectionColor)}>
              Model Context Protocol Server
            </h1>
            <p className="text-neutral-400 mt-2">
              Enable AI assistants like Claude and ChatGPT to access Line4's risk management data
            </p>
          </div>
        </div>
        <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-2 border-purple-500/30">
          <Server className="w-8 h-8 text-purple-500" />
        </div>
      </div>

      {/* Status Card */}
      <div className="card border-2 border-purple-500/30 bg-gradient-to-br from-purple-500/5 to-transparent">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
            <Server className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-lg font-semibold text-white">Server Status</h3>
              <span className="badge badge-success text-xs">Active</span>
            </div>
            <div className="space-y-2 text-sm text-neutral-300">
              <p>
                The MCP server enables AI assistants to interact with Line4's risk management data and perform operations through a standardized protocol.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-purple-500/20">
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Server Status</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-success-500" />
                    <span className="text-sm font-medium text-white">Running on port 3000</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Active Connections</p>
                  <p className="text-lg font-bold text-purple-400">3</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Tools Available</p>
                  <p className="text-lg font-bold text-cyan-400">12</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-400 mb-1">Last Request</p>
                  <p className="text-sm text-white">2 minutes ago</p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button className="btn-outline text-xs flex items-center gap-1">
                <Settings className="w-3 h-3" />
                Configure MCP
              </button>
              <button className="btn-outline text-xs flex items-center gap-1">
                <Code className="w-3 h-3" />
                View Documentation
              </button>
              <button className="btn-outline text-xs flex items-center gap-1">
                <RefreshCw className="w-3 h-3" />
                Restart Server
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">Server Configuration</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="form-label">Server Port</label>
              <input 
                type="text" 
                className="form-input w-full"
                value="3000"
                readOnly
              />
            </div>
            <div>
              <label className="form-label">Protocol Version</label>
              <input 
                type="text" 
                className="form-input w-full"
                value="1.0.0"
                readOnly
              />
            </div>
            <div>
              <label className="form-label">Endpoint</label>
              <div className="flex items-center gap-2 text-sm text-neutral-300 font-mono bg-neutral-900 px-3 py-2 rounded border border-neutral-800">
                <Globe className="w-4 h-4 text-primary-400" />
                <span>mcp://localhost:3000</span>
              </div>
            </div>
            <div>
              <label className="form-label">Max Connections</label>
              <input 
                type="text" 
                className="form-input w-full"
                value="10"
                readOnly
              />
            </div>
          </div>
        </div>
      </div>

      {/* Available Tools */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold">Available MCP Tools</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Tool Name</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Description</th>
                <th className="text-left p-4 text-sm font-medium text-neutral-400">Category</th>
                <th className="text-right p-4 text-sm font-medium text-neutral-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'getRisks', description: 'Retrieve risk register entries', category: 'Risk Management', status: 'active' },
                { name: 'createRisk', description: 'Create new risk entry', category: 'Risk Management', status: 'active' },
                { name: 'getControls', description: 'Get control library', category: 'Controls', status: 'active' },
                { name: 'getCompliance', description: 'Check compliance status', category: 'Compliance', status: 'active' },
                { name: 'runAssessment', description: 'Execute risk assessment', category: 'Assessment', status: 'active' },
                { name: 'getMetrics', description: 'Retrieve KPIs and metrics', category: 'Reporting', status: 'active' },
                { name: 'searchPolicies', description: 'Search policy documents', category: 'Policies', status: 'active' },
                { name: 'getAuditLog', description: 'Access audit trail', category: 'Audit', status: 'active' },
                { name: 'analyzeRisk', description: 'AI risk analysis', category: 'AI Analysis', status: 'beta' },
                { name: 'getSuppliers', description: 'Get supplier data', category: 'Third Party', status: 'active' },
                { name: 'createWorkflow', description: 'Create risk workflow', category: 'Workflows', status: 'active' },
                { name: 'generateReport', description: 'Generate compliance report', category: 'Reporting', status: 'active' },
              ].map((tool, index) => (
                <tr key={index} className="border-b border-neutral-800 hover:bg-neutral-800/30 transition-colors">
                  <td className="p-4">
                    <span className="text-sm font-medium text-white font-mono">{tool.name}()</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-neutral-400">{tool.description}</span>
                  </td>
                  <td className="p-4">
                    <span className="badge badge-primary text-xs">{tool.category}</span>
                  </td>
                  <td className="p-4 text-right">
                    <span className={cn(
                      "badge text-xs",
                      tool.status === 'active' ? "badge-success" : "badge-warning"
                    )}>
                      {tool.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Activity Log */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold">Recent Activity</h2>
          <Activity className="w-5 h-5 text-primary-400" />
        </div>
        <div className="p-6 space-y-3">
          {[
            { time: '2 minutes ago', action: 'getRisks() called', client: 'Claude Desktop' },
            { time: '5 minutes ago', action: 'createRisk() executed', client: 'Claude Desktop' },
            { time: '12 minutes ago', action: 'getCompliance() called', client: 'ChatGPT' },
            { time: '18 minutes ago', action: 'runAssessment() executed', client: 'Claude Desktop' },
            { time: '25 minutes ago', action: 'getMetrics() called', client: 'Claude Desktop' },
          ].map((log, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-neutral-900 rounded-lg border border-neutral-800">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
                <div>
                  <p className="text-sm text-white font-medium">{log.action}</p>
                  <p className="text-xs text-neutral-500">{log.client}</p>
                </div>
              </div>
              <span className="text-xs text-neutral-400">{log.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
